import { Component, OnInit, Input, OnChanges, EventEmitter, Output } from '@angular/core';
import { UnitsService, FormattingService, Quantity, FileSaveService } from 'pumpcalc-core';
import { SolverResultConditionsRangeVariable } from '../gasflow-solver.service';
import { Keys, Labels, LabelToKeys, PipeDimensionKeys } from '../gasflow.constants';
import { RangeVariable } from '../range-variable-form/range-variable-form.component';
import { ResultsService } from '../results.service';
import { ResultTableService, SeriesSelection } from './result-table.service';

@Component({
  selector: 'app-result-table',
  templateUrl: './result-table.component.html',
  styleUrls: ['./result-table.component.css']
})
export class ResultTableComponent implements OnInit, OnChanges {

  @Input() solverResultsAndConditonsAndRangeVariable: SolverResultConditionsRangeVariable[];
  @Input() defaultStyle = true;
  @Input() showComplex: boolean = false;
  @Output() selection: EventEmitter<SeriesSelection> = new EventEmitter();
  @Output() pinnedSelection: EventEmitter<SeriesSelection> = new EventEmitter();
  @Output() clearedSelection: EventEmitter<any> = new EventEmitter();
  results = [];
  keys = [];
  resultMeta = {};
  selectedKeys = [];
  rangedVariableKeys = [];
  visibleKeys = [];
  selectedHeaders = [];
  labels = Labels;
  isMixedResult = false;
  isRangedResult = false;
  showingSelected = false;
  showingAll = false;
  chartPinned = false;
  somethingSelected = false;
  quantity = Quantity;
  buttonToggles = {};
  lastPinnedSelectedKeys;

  constructor(public unitsService: UnitsService, public formattingService: FormattingService, public resultsService: ResultsService, private fileSaveService: FileSaveService, public resultTableService:ResultTableService) {}

  ngOnInit() {
  }

  ngOnChanges(changes: import("@angular/core").SimpleChanges): void {
    this.setResults();
  }

  setResults(){
    if (!this.solverResultsAndConditonsAndRangeVariable || !this.solverResultsAndConditonsAndRangeVariable[0]) { return; }
    this.results = [];
    this.keys = [];
    this.rangedVariableKeys = [];
    if (this.solverResultsAndConditonsAndRangeVariable[0].solverResult.results.length > 1) {
      this.setRangedResult();
    } else {
      this.setMixedResult();
    }
    if (!Object.keys(this.selectedKeys)[0]) { this.selectHeader("System", true) }
    else {this.onlyShowSelectedAndHeadersAndReferenceVariable();}
  }

  setRangedResult() {
    this.isRangedResult = true;
    let results = this.solverResultsAndConditonsAndRangeVariable[0].solverResult.results;
    let index = 1;
    for (let i = results.length - 1; i >= 0; i--) {
      let result = this.showComplex ? this.resultsService.complexResult(results[i], this.solverResultsAndConditonsAndRangeVariable[0].conditions, "#" + index, true) : this.resultsService.systemResult(results[i], this.solverResultsAndConditonsAndRangeVariable[0].conditions, "#" + index, true);
      this.results.splice(0, 0, result);
      this.setKeysAndResultMeta(this.results);
      index++;
    }
    this.resultTableService.initSelectedHeaders(this.keys,this.resultMeta,this.selectedHeaders);
    this.rangedVariableKeys[this.resultTableService.getLabelFromRangeVariable(this.solverResultsAndConditonsAndRangeVariable[0].rangeVariable[1])] = true;
    this.resultTableService.setVisibleKeysForRangedResult(this.keys,this.visibleKeys,this.resultMeta,this.rangedVariableKeys);
    if (!Object.keys(this.selectedKeys)[0]) { this.toggleValueSelection(Labels[Keys.systemUpstreamSpeed]) } else {
      this.selection.emit(this.resultTableService.getSelectedSeries(this.solverResultsAndConditonsAndRangeVariable,this.rangedVariableKeys,this.selectedKeys,this.resultMeta));
      if(this.pinnedSelection){
        this.clearSelection();
        this.setPinnedSelection(this.lastPinnedSelectedKeys);
      }
    };
  }

  setMixedResult() {
    this.isRangedResult = false;
    let multiPipeReport = this.resultTableService.isMultiPipeResult(this.solverResultsAndConditonsAndRangeVariable);
    this.solverResultsAndConditonsAndRangeVariable.forEach((solverResultsAndConditon, index) => {
      let result = this.showComplex ? this.resultsService.complexResult(solverResultsAndConditon.solverResult.results[0], solverResultsAndConditon.conditions, "#" + (index + 1), multiPipeReport) : this.resultsService.systemResult(solverResultsAndConditon.solverResult.results[0], solverResultsAndConditon.conditions, "#" + (index + 1), multiPipeReport);
      this.results.splice(0, 0, result);
    });
    this.setKeysAndResultMeta(this.results);
  }

  select(key) {
    if (this.resultMeta[key].isHeading) {this.selectHeader(key);return;}
    this.toggleValueSelection(key);
  }

  selectHeader(heading, forceSelect?: boolean){
    this.resultTableService.selectAndMakeVisibleHeaderAndReferenceVariable(heading,this.selectedHeaders,this.resultMeta,this.visibleKeys,this.rangedVariableKeys,forceSelect);
    this.showingSelected = false;
  }

  toggleValueSelection(key, forceTrue?: boolean) {
    let selectionState = this.resultTableService.selectionStateAfterToggleKeySelection(key,this.selectedKeys,this.isRangedResult,this.resultMeta,forceTrue);
    this.somethingSelected = selectionState.isSomethingSelected;
    this.selectedKeys=selectionState.selectedKeys;
    if (this.isRangedResult) {
      this.selection.emit(this.resultTableService.getSelectedSeries(this.solverResultsAndConditonsAndRangeVariable,this.rangedVariableKeys,this.selectedKeys,this.resultMeta));
    }
    if (!this.selectedKeys[key] && this.results[0][key].quantity) {this.toggleButton(this.results[0][key].quantity, false)}
  }

  selectValuesForQuantity(quantity: string) {
    this.keys.forEach(key => {
      if (key && this.resultMeta[key] && this.resultMeta[key].quantity == quantity) {
        this.toggleButton(quantity, !this.selectedKeys[key]);
        this.toggleValueSelection(key, !this.selectedKeys[key]);
      }
    })
    this.onlyShowSelectedAndHeadersAndReferenceVariable();
  }

  onlyShowSelectedAndHeadersAndReferenceVariable() {
    this.resultTableService.setSelectedKeysAndHeadersAsVisible(this.visibleKeys,this.selectedKeys,this.selectedHeaders);
    this.resultTableService.setReferenceVariableAsVisible(this.visibleKeys,this.rangedVariableKeys);
    this.showingSelected = true;
    this.showingAll = false;
  }

  showAll() {
    this.resultTableService.setAllAsVisible(this.keys,this.visibleKeys,this.selectedHeaders);
    this.showingSelected = false;
    this.showingAll = true;
  }

  pinSelection() {
    this.setPinnedSelection(this.selectedKeys);
  }

  setPinnedSelection(selection){
    this.lastPinnedSelectedKeys = this.resultTableService.cloneSelection(selection);
    this.pinnedSelection.emit(this.resultTableService.getSelectedSeries(this.solverResultsAndConditonsAndRangeVariable,this.rangedVariableKeys,selection,this.resultMeta));
  }

  clearSelection() {
    this.clearedSelection.emit("");
  }

  setReport() {
    this.setResults();
    this.showAll();
  }

  setKeysAndResultMeta(results: any[]) {
    let keysAndResultMeta = this.resultTableService.getKeysAndResultMeta(results);
    this.keys = keysAndResultMeta.keys;
    this.resultMeta = keysAndResultMeta.resultMeta;
  }

  copyResultsToClipboard() {
    this.fileSaveService.copyStringToClipBoard(this.resultTableService.getResultsAsText(this.visibleKeys,this.results));
  }

  toggleButton(name, value: boolean) {
    if (this.isRangedResult) {
      Object.keys(this.buttonToggles).forEach(key => {
        this.buttonToggles[key] = false;
      });
    }
    this.buttonToggles[name] = value;
  }



}

